/**
 * countUpモジュール
 * 数値をカウントアップして表示するアニメーション
 *
 * @module countUp
 * @param {string} selector カウント対象要素のセレクタ
 * @param {Object} options オプション設定
 * @param {number} options.duration アニメーション時間（デフォルト: 2000ms）
 * @param {number} options.delay ディレイ時間（デフォルト: 0ms）
 * @param {null | object} options.root IntersectionObserverのroot
 * @param {string} options.rootMargin IntersectionObserverのrootMargin
 * @param {number} options.threshold IntersectionObserverのthreshold
 */
export default function countUp(selector, options = {}) {
  const defaultOptions = {
    duration: 2000,
    delay: 0,
    root: null,
    rootMargin: '0px 0px',
    threshold: 0,
  };

  const settings = { ...defaultOptions, ...options };
  const targets = document.querySelectorAll(selector);

  if (targets.length === 0) return;

  // 既にカウント済みのフラグを管理するWeakMap
  const countedElements = new WeakMap();

  const observerOptions = {
    root: settings.root,
    rootMargin: settings.rootMargin,
    threshold: settings.threshold,
  };

  function animateCount(element) {
    const countValue = parseInt(element.dataset.count, 10);
    const startValue = 0;
    const startTime = performance.now();

    // 既にカウント済みなら実行しない
    if (countedElements.get(element)) {
      return;
    }

    function step(currentTime) {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / settings.duration, 1);
      const currentValue = Math.floor(startValue + (countValue - startValue) * progress);

      // spanタグに数値を表示
      const span = element.querySelector('span');
      if (span) {
        span.textContent = currentValue.toLocaleString();
      } else {
        element.textContent = currentValue.toLocaleString();
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // アニメーション完了後、正確な値を表示
        if (span) {
          span.textContent = countValue.toLocaleString();
        } else {
          element.textContent = countValue.toLocaleString();
        }
        // カウント完了フラグを立てる
        countedElements.set(element, true);
      }
    }

    requestAnimationFrame(step);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !countedElements.get(entry.target)) {
        setTimeout(() => {
          animateCount(entry.target);
        }, settings.delay);
      }
    });
  }, observerOptions);

  targets.forEach((target) => {
    observer.observe(target);
  });
}
